/**
 * File uploads and downloads — Nest `/files/upload`, Firebase school reports,
 * student import template (.xlsx from the API).
 */

import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '@/config/storage';
import { XLSX_MIME } from '@/constants/mime-types.constants';
import apiClient from './api-client';

/** `POST /files/upload` — returns the stored file URL. */
export const uploadFile = async (file: File): Promise<string> => {
  const body = new FormData();
  body.append('file', file);
  const { data } = await apiClient.post<{ url: string }>('/files/upload', body);
  return data.url;
};

/** Upload a remote-student school report to Firebase Storage. */
export async function uploadStudentSchoolReport(
  file: File,
  companyId: string,
  studentUserId: string,
): Promise<string> {
  const safeName = file.name.replace(/[/\\]/g, '_') || 'bulletin.pdf';
  const objectPath = `companies/${companyId}/students/${studentUserId}/school-reports/${Date.now()}_${safeName}`;
  const storageRef = ref(storage, objectPath);
  const bytes = await file.arrayBuffer();
  await uploadBytes(storageRef, bytes, {
    contentType: file.type || (safeName.endsWith('.pdf') ? 'application/pdf' : undefined),
  });
  return getDownloadURL(storageRef);
}

/**
 * `GET /users/students/import-template` — in-person student import template (.xlsx) generated
 * by the API with dropdowns for gender, establishment (actor scope) and level.
 */
export async function downloadStudentImportTemplate(filename: string): Promise<void> {
  const response = await apiClient.get('/users/students/import-template', {
    responseType: 'blob',
  });
  const blob = new Blob([response.data], { type: XLSX_MIME });
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(objectUrl);
}
